import {
  computed,
  getCurrentInstance,
  ref,
  type Ref,
  type WritableComputedRef,
} from 'vue'
import type { Pinia } from 'pinia'
import { useContextStore } from 'src/store'

const SSR_STATE_KEY = '__ssr_state'

/**
 * Хранилище данных, общих для SSR и клиента.
 *
 * Используется для передачи результатов API-запросов, выполненных во время
 * SSR, чтобы после гидратации не выполнять эти же запросы повторно.
 *
 * Если Pinia недоступна, возвращается обычный локальный `ref`.
 */
export default function useSsrState<T>(): Ref<T | undefined> {
  const instance = getCurrentInstance()
  const pinia = (instance?.proxy as { $pinia?: Pinia } | undefined)?.$pinia

  if (!pinia) {
    return ref<T | undefined>()
  }

  const { context } = useContextStore(pinia)

  if (!(SSR_STATE_KEY in context)) {
    context[SSR_STATE_KEY] = undefined
  }

  return computed({
    get: () => context[SSR_STATE_KEY] as T | undefined,
    set: (value: T | undefined) => {
      context[SSR_STATE_KEY] = value
    },
  }) as WritableComputedRef<T | undefined>
}
